import { useState, useRef, useEffect, useCallback } from "react";

interface Position {
  top: number;
  left: number;
}

/**
 * Keeps a floating menu centered on a trigger element as the trigger moves
 * or its surroundings change.
 *
 * Typical use: a table row has a "⋯" button that opens a menu rendered
 * through a React portal. Because the portal is detached from the row in
 * the DOM, we need to tell it where to appear on screen.
 *
 * Attach `triggerRef` to the anchor element and use `position` (top/left
 * in pixels) to place the portal with `position: fixed`.
 *
 * The position updates when:
 * - The window is resized.
 * - Any scrollable area scrolls.
 * - The trigger or any of its nearby ancestors changes size. This catches
 *   the case where a sibling row expands (e.g. a collapsible column) and
 *   shifts this trigger down, because `ResizeObserver` only fires on size
 *   changes — not on position changes. Observing a shared ancestor (whose
 *   total height grows when any sibling row grows) is how we detect
 *   position shifts caused by surrounding layout changes.
 *
 * Performance: all updates share one `requestAnimationFrame` per frame, so
 * rapid events like scrolling or several observers firing in the same tick
 * only trigger one recalculation per frame. Also, the state only updates
 * when the position actually changes, to avoid unnecessary re-renders.
 */
export const useFloatingMenu = () => {
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLElement | null>(null);

  /** Reads the trigger's current rect and centers a 28×28 menu on it. */
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = 28;
    const menuWidth = 28;

    const top = Math.max(10, rect.top + rect.height / 2 - menuHeight / 2);
    const left = Math.max(10, rect.left + rect.width / 2 - menuWidth / 2);

    setPosition(prev => (prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  useEffect(() => {
    if (!triggerRef.current) return;

    let rafId = 0;

    /** Groups multiple reposition requests in the same frame into one call. */
    const scheduleReposition = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        calculatePosition();
      });
    };

    scheduleReposition();

    window.addEventListener("resize", scheduleReposition);
    window.addEventListener("scroll", scheduleReposition, true);

    // Observe the trigger and every ancestor up to <body>. `ResizeObserver`
    // only fires on size changes, not on position changes, so observing
    // only the trigger or its row misses the case where a sibling row
    // grows and pushes this one down. A shared ancestor (row container,
    // table, etc.) always changes size when any sibling grows, which is
    // what lets us detect the displacement. Walking all the way to <body>
    // avoids depending on a specific DOM depth, which varies by library
    // (e.g. table wrappers can add several extra levels). rAF coalescing
    // keeps the cost at one recalculation per frame regardless of how
    // many observers fire.
    const resizeObserver = new ResizeObserver(scheduleReposition);
    let node: Element | null = triggerRef.current;
    while (node) {
      resizeObserver.observe(node);
      if (node === document.body) break;
      node = node.parentElement;
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", scheduleReposition);
      window.removeEventListener("scroll", scheduleReposition, true);
      resizeObserver.disconnect();
    };
  }, [calculatePosition]);

  return {
    position,
    triggerRef,
  };
};
